import { createContext, FC, useContext, useMemo } from 'react';
import { isFunction } from 'lodash';
import { QueryResult, useQuery } from '@apollo/client';
import {
	DynamicPermissionCode,
	GetLanguagesList,
	GetTimezonesList
} from 'Services/Api/Users/Types';
import { GetSystemRolesList, GetBaseSystemRolesResponse, SystemRoleType, SystemRolesListItem } from 'Services/Api/Roles/Types';
import { GET_LANGUAGES, GET_TIMEZONES } from 'Services/Api/Users/Queries';
import { GET_SYSTEM_ROLES_LIST, GET_BASE_SYSTEM_ROLES } from 'Services/Api/Roles/Queries';

interface UserContextValue {
	languages: QueryResult<GetLanguagesList>;
	languagesList: GetLanguagesList['getLanguagesList'] | undefined;
	timezones: QueryResult<GetTimezonesList>;
	timezonesList: GetTimezonesList['getTimezonesList'] | undefined;
	systemRoles: QueryResult<GetSystemRolesList>;
	systemRolesList: GetSystemRolesList['getSystemRolesList'] | undefined;
}

interface UserContextProviderProps {
	permissionCode: DynamicPermissionCode;
	children:
		| React.ReactNode
		| ((helpers: { loading: boolean }) => React.ReactNode);
}

export const UserContext = createContext<UserContextValue>(
	{} as UserContextValue
);

const DEFAULT_BASE_ROLES: GetSystemRolesList['getSystemRolesList'] = [
	{ id: 1, role: SystemRoleType.Admin, isPrimary: true, permissions: [], twoFactorAuthorizationEnabled: false },
	{ id: 2, role: SystemRoleType.User, isPrimary: true, permissions: [], twoFactorAuthorizationEnabled: false },
	{ id: 3, role: SystemRoleType.Manager, isPrimary: true, permissions: [], twoFactorAuthorizationEnabled: false },
	{ id: 4, role: SystemRoleType.SuperAdmin, isPrimary: true, permissions: [], twoFactorAuthorizationEnabled: false },
	{ id: 5, role: SystemRoleType.Owner, isPrimary: true, permissions: [], twoFactorAuthorizationEnabled: false }
];

const UserContextProvider: FC<UserContextProviderProps> = ({
	permissionCode,
	children
}) => {
	const languages = useQuery<GetLanguagesList>(GET_LANGUAGES, {
		fetchPolicy: 'cache-first'
	});
	const timezones = useQuery<GetTimezonesList>(GET_TIMEZONES, {
		fetchPolicy: 'cache-first'
	});
	const systemRoles = useQuery<GetSystemRolesList>(GET_SYSTEM_ROLES_LIST, {
		variables: {
			action: permissionCode
		}
	});
	const baseSystemRoles = useQuery<GetBaseSystemRolesResponse>(GET_BASE_SYSTEM_ROLES, {
		fetchPolicy: 'cache-first'
	});

	const systemRolesList = useMemo(() => {
		if (systemRoles.data?.getSystemRolesList && systemRoles.data.getSystemRolesList.length > 0) {
			return systemRoles.data.getSystemRolesList;
		}
		if (baseSystemRoles.data?.getBaseSystemRoles && baseSystemRoles.data.getBaseSystemRoles.length > 0) {
			return baseSystemRoles.data.getBaseSystemRoles;
		}
		return DEFAULT_BASE_ROLES;
	}, [systemRoles.data?.getSystemRolesList, baseSystemRoles.data?.getBaseSystemRoles]);

	const loading =
		!languages.called ||
		languages.loading ||
		!timezones.called ||
		timezones.loading ||
		!systemRoles.called ||
		systemRoles.loading;

	return (
		<UserContext.Provider
			value={{
				languages,
				languagesList: languages.data?.getLanguagesList,
				timezones,
				timezonesList: timezones.data?.getTimezonesList,
				systemRoles,
				systemRolesList
			}}
		>
			{isFunction(children) ? children({ loading }) : children}
		</UserContext.Provider>
	);
};

export const useUserContext = () => useContext(UserContext);

export default UserContextProvider;
